import { Building2, ClipboardCheck, Download, FileText, Sheet, UsersRound } from 'lucide-react';
import { useState } from 'react';

import { AdminPage } from '@/components/admin/AdminPage';
import { EmptyState, ErrorState } from '@/components/ui/empty-state';
import { FilterPills } from '@/components/ui/filter-pills';
import { KpiCard } from '@/components/ui/kpi-card';
import { Pagination } from '@/components/ui/pagination';
import { StatusBadge } from '@/components/ui/status-badge';
import {
  reportDownloadUrl,
  useReport,
  useReportKpis,
  useReportRuns,
  type ReportParams,
} from '@/hooks/reports';
import { formatDay, formatInstant } from '@/lib/dates';
import { formatCents } from '@shared/money';
import { formatMinutes } from '@shared/overtime';
import {
  REPORT_PRESET_LABELS,
  REPORT_TYPE_DESCRIPTIONS,
  REPORT_TYPE_LABELS,
  REPORT_TYPES,
  type ReportCell,
  type ReportColumn,
  type ReportPreset,
  type ReportTable,
  type ReportType,
} from '@shared/schemas/report';

/*
 * /admin/reports — ROADMAP.md §10.5.
 *
 * ## The four cards are the whole point of this slice
 *
 * **Defect P1**: in the prototype these four show `—` permanently. A network
 * trace on page load fires `auth/me` and `notifications` and nothing else — they
 * are decoration, and a dash reads as zero at a glance while meaning "unknown".
 *
 * They are its four, by name, so the two screens can be held side by side:
 * **Total Jobs, Completed, Active Employees, Active Clients.** Here they fetch,
 * they show a skeleton while loading, and a business with no jobs this month
 * shows `0` — which is a fact.
 *
 * `KpiCard` is what makes that structural rather than careful: `value` is
 * `number | null`, there is no way to hand it a dash, and `null` renders a
 * sentence explaining itself instead.
 *
 * ---
 *
 * **The report is shown before it is downloaded.** Looking at one is the common
 * case; exporting is not, and a screen that can only produce a file makes
 * somebody open a spreadsheet to answer "how many jobs did we do last month".
 * The `report_run` history records what was exported, not what was looked at.
 */

const PRESETS: { value: ReportPreset; label: string }[] = [
  { value: 'today', label: REPORT_PRESET_LABELS.today },
  { value: 'last_7', label: REPORT_PRESET_LABELS.last_7 },
  { value: 'last_30', label: REPORT_PRESET_LABELS.last_30 },
  { value: 'last_year', label: REPORT_PRESET_LABELS.last_year },
];

export function ReportsPage() {
  const [type, setType] = useState<ReportType>('summary');
  const [preset, setPreset] = useState<ReportPreset>('last_30');
  const [historyPage, setHistoryPage] = useState(1);

  const params: ReportParams = { type, preset };

  const report = useReport(params);
  const kpis = useReportKpis(params);
  const runs = useReportRuns(historyPage);

  return (
    <AdminPage
      title="Reports"
      description="What the business did over a period, as a table you can read or a file you can send on."
    >
      {/*
        **The four cards — P1.** Numbers, always. `isLoading` shows a skeleton
        and a zero shows `0`; there is no state in which these render a dash.
      */}
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          label="Total jobs"
          value={kpis.data?.totalJobs ?? null}
          isLoading={kpis.isPending}
          icon={ClipboardCheck}
          subLabel={
            kpis.data ? `${formatDay(kpis.data.from)} – ${formatDay(kpis.data.to)}` : undefined
          }
        />
        <KpiCard
          label="Completed"
          value={kpis.data?.completedJobs ?? null}
          isLoading={kpis.isPending}
          icon={ClipboardCheck}
        />
        <KpiCard
          label="Active employees"
          value={kpis.data?.activeEmployees ?? null}
          isLoading={kpis.isPending}
          icon={UsersRound}
          subLabel="Right now"
        />
        <KpiCard
          label="Active clients"
          value={kpis.data?.activeClients ?? null}
          isLoading={kpis.isPending}
          icon={Building2}
          subLabel="Right now"
        />
      </div>

      {/*
        The cards failing is an error, not four empty cards — P22. It is its own
        message because the cards and the table fetch separately and either can
        fail alone.
      */}
      {kpis.error ? (
        <div className="mb-4 rounded-lg border border-ink-300 bg-white shadow-card">
          <ErrorState
            onRetry={() => {
              void kpis.refetch();
            }}
          />
        </div>
      ) : null}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <FilterPills
          label="Choose a report"
          options={REPORT_TYPES.map((value) => ({ value, label: REPORT_TYPE_LABELS[value] }))}
          value={type}
          onChange={setType}
        />

        <div className="ml-auto">
          <FilterPills
            label="Choose a period"
            options={PRESETS}
            value={preset}
            onChange={setPreset}
          />
        </div>
      </div>

      <p className="mb-4 text-body text-ink-500">{REPORT_TYPE_DESCRIPTIONS[type]}</p>

      <div className="mb-4 flex flex-wrap gap-2">
        {/*
          **Plain links, not fetches.** The browser downloads a file better than
          a blob URL and a synthetic click do — progress, resume, and the right
          folder — and the session is a cookie so the link authenticates itself.
          `download` is what stops a CSV being rendered as text in a tab.
        */}
        <a
          href={reportDownloadUrl(params, 'csv')}
          download
          className="inline-flex min-h-touch items-center gap-2 rounded-md border border-ink-300 bg-white px-4 text-label text-ink-900 hover:bg-honey-50"
        >
          <Sheet aria-hidden="true" className="size-4" />
          Download CSV
        </a>
        <a
          href={reportDownloadUrl(params, 'pdf')}
          download
          className="inline-flex min-h-touch items-center gap-2 rounded-md border border-ink-300 bg-white px-4 text-label text-ink-900 hover:bg-honey-50"
        >
          <FileText aria-hidden="true" className="size-4" />
          Download PDF
        </a>
      </div>

      {report.error ? (
        <div className="rounded-lg border border-ink-300 bg-white shadow-card">
          <ErrorState
            onRetry={() => {
              void report.refetch();
            }}
          />
        </div>
      ) : report.isPending ? (
        <div aria-busy="true">
          <span className="sr-only">Loading the report</span>
          <div aria-hidden="true" className="h-64 animate-pulse rounded-lg bg-ink-100" />
        </div>
      ) : report.data ? (
        <ReportPreview table={report.data} />
      ) : null}

      <h2 className="mb-3 mt-8 text-h2 text-ink-900">Reports you have exported</h2>

      <p className="mb-3 text-meta text-ink-500">
        {/*
          Said plainly rather than left to be discovered: there is no file to
          click, and that is a decision (migration 023) rather than a missing
          link somebody should go looking for.
        */}
        The file itself is not kept — the settings are, so any of these can be run again with the
        same window. A different row count on a re-run means something underneath changed.
      </p>

      {runs.error ? (
        <div className="rounded-lg border border-ink-300 bg-white shadow-card">
          <ErrorState
            onRetry={() => {
              void runs.refetch();
            }}
          />
        </div>
      ) : runs.data && runs.data.data.length > 0 ? (
        <>
          <ul className="divide-y divide-ink-100 overflow-hidden rounded-lg border border-ink-300 bg-white shadow-card">
            {runs.data.data.map((run) => (
              <li
                key={run.id}
                className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
              >
                <div>
                  <p className="text-body text-ink-900">
                    {/* The label, never the stored value — P5, even here. */}
                    {run.reportTypeLabel} · {run.format.toUpperCase()}
                    <span className="ml-2 text-ink-500">{run.rangeLabel}</span>
                  </p>
                  <p className="text-meta text-ink-500">
                    {run.requestedByName} · {formatInstant(run.createdAt)}
                    {run.rowCount === null ? null : (
                      <> · {run.rowCount === 1 ? '1 row' : `${String(run.rowCount)} rows`}</>
                    )}
                  </p>
                  {run.errorMessage ? (
                    <p className="mt-1 text-meta text-ink-900">{run.errorMessage}</p>
                  ) : null}
                </div>

                <StatusBadge
                  label={run.statusLabel}
                  tone={
                    run.status === 'ok'
                      ? 'completed'
                      : run.status === 'error'
                        ? 'cancelled'
                        : 'pending'
                  }
                />
              </li>
            ))}
          </ul>

          <Pagination
            page={historyPage}
            pageSize={runs.data.pageSize}
            total={runs.data.total}
            onPageChange={setHistoryPage}
            itemNoun="exports"
          />
        </>
      ) : (
        <div className="rounded-lg border border-ink-300 bg-white shadow-card">
          <EmptyState
            icon={Download}
            title="Nothing has been exported yet."
            hint="Downloading a report records it here, with the window it covered."
          />
        </div>
      )}
    </AdminPage>
  );
}

/**
 * One value, formatted for a screen.
 *
 * The server sends integer cents and whole minutes and never a formatted string
 * — that is what lets the CSV ship a number a spreadsheet can total. Formatting
 * happens here, at the display edge, exactly as it does in each renderer.
 */
function cell(value: ReportCell, kind: ReportColumn['kind']): string {
  if (value === null) return '';

  switch (kind) {
    case 'money':
      return typeof value === 'number' ? formatCents(value) : String(value);
    case 'minutes':
      return typeof value === 'number' ? formatMinutes(value) : String(value);
    case 'date':
      return typeof value === 'string' ? formatDay(value) : String(value);
    default:
      return String(value);
  }
}

function ReportPreview({ table }: { table: ReportTable }) {
  if (table.rows.length === 0) {
    return (
      <div className="rounded-lg border border-ink-300 bg-white shadow-card">
        {/*
          **An empty report is an answer, not a failure.** It says which window
          it looked at, so the reader can tell "nothing happened" from "wrong
          dates" — which is the question they are actually asking.
        */}
        <EmptyState
          icon={FileText}
          title={`Nothing to report for ${table.subtitle}.`}
          hint="Try a longer period, or a different report."
        />
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-ink-300 bg-white shadow-card">
      <div className="border-b border-ink-300 px-4 py-3">
        <h2 className="text-h3 text-ink-900">{table.title}</h2>
        <p className="text-meta text-ink-500">
          {table.subtitle} ·{' '}
          {table.rows.length === 1 ? '1 row' : `${String(table.rows.length)} rows`}
        </p>
      </div>

      {/*
        Scrolls inside its own box rather than making the page scroll sideways.
        The Jobs report is seven columns and this is opened on a laptop.
      */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[40rem] text-body">
          <caption className="sr-only">
            {table.title}, {table.subtitle}
          </caption>
          <thead>
            <tr className="border-b border-ink-300 text-meta uppercase tracking-wide text-ink-500">
              {table.columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  className={
                    column.kind === 'text'
                      ? 'px-4 py-2 text-left font-medium'
                      : 'px-4 py-2 text-right font-medium'
                  }
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row, rowIndex) => (
              // The index is the key because a report row has no id — it is a
              // computed aggregate, and the list is replaced wholesale on every
              // fetch rather than reordered.
              <tr key={rowIndex} className="border-b border-ink-100 last:border-0">
                {row.map((value, index) => {
                  const column = table.columns[index]!;
                  /*
                   * **A row's own kind wins over its column's** — the Summary
                   * report's value column carries a count, hours and money in
                   * different rows, and typed by column alone it rendered the
                   * stored integers: `4560` where `76h 00m` belongs. Found on
                   * the screen.
                   */
                  const kind =
                    index === 0 ? column.kind : (table.rowKinds?.[rowIndex] ?? column.kind);
                  return (
                    <td
                      key={column.key}
                      className={
                        column.kind === 'text'
                          ? 'px-4 py-2 text-left'
                          : 'px-4 py-2 text-right tabular-nums'
                      }
                    >
                      {cell(value, kind)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>

          {table.totals ? (
            <tfoot>
              <tr className="border-t-2 border-ink-300 font-medium">
                {table.totals.map((value, index) => {
                  const column = table.columns[index]!;
                  return (
                    <td
                      key={column.key}
                      className={
                        column.kind === 'text'
                          ? 'px-4 py-2 text-left'
                          : 'px-4 py-2 text-right tabular-nums'
                      }
                    >
                      {cell(value, column.kind)}
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>
    </div>
  );
}
